/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React from "react";
import PropTypes from "prop-types";

import useStore from "@/stores/index";
import { userDisplayText } from "@/js/utils/userUtils";
import { getClassroomIdForChat, getChatDisabled, isChatFeatureEnabled } from "@/js/utils/chatUtils";
import SendMessageModal from "../SendMessageModal";
import { StudentTile } from "../StudentTile";
import { getClasswizeConfig } from "@/stores/classwizeConfigStore";
import { StudentTileHeader } from "../StudentTile/StudentTileHeader";
import { StudentTileContent } from "../StudentTile/StudentTileContent";
import { togglePauseForStudent } from "@/api/pause";
import { togglePauseForStudentInState } from "../RegularClassroom/utils";
import { StudentTileButton } from "./components/StudentTileButton";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { isPlatformChatSupported } from "@/js/utils/chatUtils";

export default class RegularClassroomUser extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			singleMessageModalOpen: false,
			singleNewTaModalOpen: false,
			unreadMessagesCount: 0,
			threadKey: useStore.getState().chat.getThreadKey(this.props.classroom, this.props.user),
			disabledChat: true,
		};

		this.classroomSourceId = useUserSessionStore.getState().getActiveGroup();
		this.unsubMessages = null;
	}

	unsubClassroomStore = useStore.subscribe(
		(state) => [state.classroom.dataV2],
		() => this.handleClassroomUpdate()
	);

	handleClassroomUpdate = () => {
		const chatIsDisabled = getChatDisabled(this.props.classroom.name);
		this.setState({
			disabledChat: chatIsDisabled,
		});
	};

	componentDidMount() {
		const chatIsDisabled = getChatDisabled(this.props.classroom.name);
		this.setState({
			disabledChat: chatIsDisabled,
		});
	}

	componentWillUnmount() {
		this.unsubClassroomStore();
	}

	handleTogglePause = () => {
		const teacherId = useStore.getState().classroom.getTeacherStableIdV1();
		const classroomId = this.props.classroom.stableIdV1;
		const studentId = this.props.user.username;
		togglePauseForStudent(teacherId, classroomId, studentId)
			.then(() => {
				togglePauseForStudentInState(this.props.user.username);
			})
			.catch(() => {
				console.error("Failed to toggle pause for student");
			});
	};

	handleToggleFocus = () => {
		this.props.handleToggleFocusForStudent(this.props.user.stableIdV1);
	};

	isPauseInternetEnabled = () => {
		const classwizeConfig = getClasswizeConfig(useStore.getState());
		return classwizeConfig && (classwizeConfig.classroom_features_enabled?.includes("lock") ?? false);
	};

	handleToggleSingleMessageClass = async () => {
		this.setState({
			singleMessageModalOpen: !this.state.singleMessageModalOpen,
		});
	};

	handleSingleChat = (threadKey) => {
		// TODO refactor this logic block to immediately select classroom student and threadlist
		useStore.getState().chat.setSelectedClassId(getClassroomIdForChat(this.props.classroom));
		useStore.getState().chat.setSelectedStudentName(this.props.user.username);

		// set selected thread
		useStore.getState().chat.setSelectedThreadKey(threadKey);

		if (!useStore.getState().chat.chatWindowOpen) {
			useStore.getState().chat.toggleChatWindow();
		}
	};

	handleTabsViewShowOrHideTabs = () => {
		this.setState({
			tabsViewShowHiddenTabs: !this.state.tabsViewShowHiddenTabs,
		});
	};

	isStudentPaused() {
		return useClassroomPolicyStore.getState().getPausedStudents().indexOf(this.props.user.username) >= 0;
	}

	sendMessageModalTitle = () => {
		return `Send message to ${userDisplayText(this.props.user)}`;
	};

	isChatSupported = (windowData) => {
		return isPlatformChatSupported(windowData);
	};

	render() {
		const studentDisplayName = userDisplayText(this.props.user);
		const isChatButtonDisabled = !this.props.classIsActive || !this.isChatSupported(this.props.windowData);

		return (
			<StudentTile username={this.props.user.username}>
				<StudentTileHeader
					user={this.props.user}
					actionsJsx={
						<>
							{isChatFeatureEnabled(this.props.windowData) && !this.state.disabledChat && (
								<StudentTileButton
									tooltipText={
										!this.props.classIsActive
											? ""
											: !this.isChatSupported(this.props.windowData)
												? "Chat not supported on this device"
												: `Start a Chat with ${userDisplayText(this.props.user)}`
									}
									icon="fa-message-lines"
									handleClick={() => this.handleSingleChat(this.state.threadKey)}
									disabled={isChatButtonDisabled}
									buttonTestId="student-chat-btn"
								/>
							)}
							{this.props.showStudentAnnouncementBtn && (
								<StudentTileButton
									tooltipText={!this.props.classIsActive ? "" : `Send a message to ${studentDisplayName}`}
									icon="fa-bullhorn"
									handleClick={this.handleToggleSingleMessageClass}
									disabled={!this.props.classIsActive}
									buttonTestId="student-announcement-btn"
								/>
							)}
							{this.props.showOpenTabBtn && (
								<StudentTileButton
									tooltipText={!this.props.classIsActive ? "" : `Open a tab for ${studentDisplayName}`}
									icon="fa-browser"
									handleClick={() => this.props.openNewTabModal(this.props.user)}
									disabled={!this.props.classIsActive || this.props.studentIsFocused}
									buttonTestId="student-opentab-btn"
								/>
							)}
							{this.isPauseInternetEnabled() && (
								<StudentTileButton
									tooltipText={
										!this.props.classIsActive ? "" : `${this.isStudentPaused() ? "Resume" : "Pause"} internet for ${studentDisplayName}`
									}
									icon="fa-pause"
									handleClick={this.handleTogglePause}
									disabled={!this.props.classIsActive}
									buttonTestId={"pause-student-btn"}
									isActive={this.isStudentPaused()}
									activeVariant="red-outline"
								/>
							)}
							{this.props.studentsAreFocused && (
								<StudentTileButton
									tooltipText={
										this.props.studentIsFocused
											? `End focus for ${studentDisplayName}`
											: !this.props.classIsActive
												? ""
												: this.props.activeFocusIsLocked && this.props.studentIsBeingScreenshared
													? `Cannot add ${studentDisplayName} to locked focus while screensharing`
													: `Add ${studentDisplayName} to the active focus`
									}
									icon="fa-arrows-to-eye"
									handleClick={this.handleToggleFocus}
									disabled={
										!this.props.classIsActive ||
										(!this.props.studentIsFocused && this.props.activeFocusIsLocked && this.props.studentIsBeingScreenshared)
									}
									buttonTestId={"focus-student-btn"}
									isActive={this.props.studentIsFocused}
									activeVariant="blue-solid"
									actionIsLoading={this.props.isUpdatingFocus}
								/>
							)}
						</>
					}
				/>
				<StudentTileContent
					user={this.props.user}
					view={this.props.view}
					canCloseTab={this.props.canCloseTab}
					isClassActive={this.props.classIsActive}
					windowData={this.props.windowData}
					signatureDb={this.props.signatureDb}
					classroom={this.props.classroom}
					openP2pModal={this.props.openP2pModal}
					tabItemLimit={this.props.tabItemLimit}
				/>

				<SendMessageModal
					open={this.state.singleMessageModalOpen}
					title={this.sendMessageModalTitle()}
					classroomSourceId={this.classroomSourceId}
					users={[this.props.user]}
					onClose={() => this.setState({ singleMessageModalOpen: false })}
					subtitle="Your message will appear on this student's screen. They will not be able to reply."
				/>
			</StudentTile>
		);
	}
}

RegularClassroomUser.propTypes = {
	user: PropTypes.object,
	classroom: PropTypes.object,
	windowData: PropTypes.object,
	deviceIndex: PropTypes.number,
	view: PropTypes.string,
	classIsActive: PropTypes.bool,
	canCloseTab: PropTypes.bool,
	showOpenTabBtn: PropTypes.bool,
	showStudentAnnouncementBtn: PropTypes.bool,
	openNewTabModal: PropTypes.func,
	openP2pModal: PropTypes.func,
	signatureDb: PropTypes.object,
	tabItemLimit: PropTypes.number,
	studentsAreFocused: PropTypes.bool,
	studentIsFocused: PropTypes.bool,
	handleToggleFocusForStudent: PropTypes.func,
	isUpdatingFocus: PropTypes.bool,
	activeFocusIsLocked: PropTypes.bool,
	studentIsBeingScreenshared: PropTypes.bool,
};
